import { nanoid } from 'nanoid';
import { IPatient } from './ICareMap';

// Medications/allergies/diagnoses entries historically have no unique entry id -- only an
// options-list id (allergyId/diagnosisId), which changes on re-select and is absent for
// custom entries -- so it can't be used as a stable key for the per-entry structured-record
// editor. Backfill a nanoid() id into any entry that lacks one, establishing the invariant
// that every entry in CareMapState's _activeCaremap always has an id. In memory only -- no
// server write -- it persists automatically the next time this patient is saved via
// updatePatient. Idempotent: entries that already have an id (including ones backfilled on a
// previous activation/merge of the same caremap) are left untouched, so this never generates a
// new id twice for one entry.
//
// A free function (not a CareMapState method) so it can be unit-tested directly alongside
// CareMapMerger -- CareMapState.ts pulls in Ionic components transitively (via FlowDownloader),
// which don't load under the mocha/ts-node CommonJS test runner.
//
// Called from every point CareMapState sets _activeCaremap from a fresh server/merge result --
// setActiveCaremap AND every CareMapMerger.merge(...) call site (fetchAndMergeServerState, and
// both the normal-save and 409-retry branches of save()) -- because a merge can replace a
// legacy (not-yet-resaved) caremap's arrays with the server's id-less copy, silently stripping a
// previously-backfilled id out from under an open record editor and causing
// StructuredRecordFormState's id-based findIndex to miss and push a duplicate entry on the next
// save. Deliberately NOT applied to lastApiResponse (the pristine merge baseline), which must
// stay the server's literal truth.
export const backfillStructuredRecordIds = (patient: IPatient): IPatient => {
    const withIds = <T extends { id?: string }>(records?: T[]) => records?.map(record => record.id ? record : { ...record, id: nanoid() });

    return {
        ...patient,
        medications: withIds(patient.medications),
        allergies: withIds(patient.allergies),
        diagnoses: withIds(patient.diagnoses)
    };
};
